/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Canonical catalog of the built-in example projects that ship with AIMS.
 * This is the single source of truth, consumed by:
 *   - `examplesDrawer.tsx` — the left-edge Examples drawer UI.
 *   - the agent tool `listExampleProjects` (src/agent/*) — so the AI can
 *     point a user at a relevant example WITHOUT loading it (loading would
 *     replace the user's current work; the agent deliberately has no tool to
 *     open an example).
 *
 * Each leaf pairs the Firestore project `title` (what the drawer passes to
 * fetchProject) with the i18n `labelKey` used to render the menu label.
 * Pure data + helpers — no React, no antd — so it's safe to import from the
 * agent layer.
 */

export type ExampleLeaf = { title: string; labelKey: string };
export type ExampleGroup = { labelKey: string; key: string; children: Array<ExampleLeaf | ExampleGroup> };
export const isExampleGroup = (n: ExampleLeaf | ExampleGroup): n is ExampleGroup => 'children' in n;

export const EXAMPLE_GROUPS: ExampleGroup[] = [
  {
    key: 'chemistry',
    labelKey: 'menu.examples.chemistrySubMenu',
    children: [
      {
        key: 'gas-laws',
        labelKey: 'menu.examples.chemistry.GasLaws',
        children: [
          { title: 'Gas Laws of Argon', labelKey: 'menu.examples.chemistry.GasLawsOfArgon' },
          {
            title: 'Gas Laws of Hydrogen Molecules',
            labelKey: 'menu.examples.chemistry.GasLawsOfHydrogenMolecules',
          },
        ],
      },
      {
        key: 'simple-molecules',
        labelKey: 'menu.examples.chemistry.SimpleMolecules',
        children: [
          { title: 'Monatomic Molecules', labelKey: 'menu.examples.chemistry.MonatomicMolecules' },
          { title: 'Diatomic Molecules', labelKey: 'menu.examples.chemistry.DiatomicMolecules' },
          { title: 'Triatomic Molecules', labelKey: 'menu.examples.chemistry.TriatomicMolecules' },
        ],
      },
      {
        key: 'speed-distributions',
        labelKey: 'menu.examples.chemistry.SpeedDistributions',
        children: [
          {
            title: 'Maxwell-Boltzmann Speed Distribution of Argon',
            labelKey: 'menu.examples.chemistry.MaxwellBoltzmannSpeedDistributionArgon',
          },
          {
            title: 'Speed Distributions of Carbon and Hydrogen Atoms in Molecules',
            labelKey: 'menu.examples.chemistry.SpeedDistributionsOfCarbonAndHydrogenInMolecules',
          },
        ],
      },
      {
        key: 'states-of-matter',
        labelKey: 'menu.examples.chemistry.StatesOfMatter',
        children: [
          { title: 'Liquid in a Box', labelKey: 'menu.examples.chemistry.LiquidInBox' },
          { title: 'Solid vs Gas', labelKey: 'menu.examples.chemistry.SolidVsGas' },
        ],
      },
      {
        key: 'phase-change',
        labelKey: 'menu.examples.chemistry.PhaseChange',
        children: [
          {
            key: 'melting',
            labelKey: 'menu.examples.chemistry.Melting',
            children: [
              { title: 'Melting of Copper', labelKey: 'menu.examples.materialsScience.MeltingOfCopper' },
              { title: 'Melting of Salt', labelKey: 'menu.examples.materialsScience.MeltingOfSalt' },
              { title: 'Melting of Silicon', labelKey: 'menu.examples.materialsScience.MeltingOfSilicon' },
              {
                title: 'Melting of Cadmium Telluride',
                labelKey: 'menu.examples.materialsScience.MeltingOfCadmiumTelluride',
              },
              { title: 'Melting of Ice', labelKey: 'menu.examples.materialsScience.MeltingOfIce' },
            ],
          },
          {
            key: 'crystal-growth-from-seed',
            labelKey: 'menu.examples.chemistry.CrystalGrowthFromSeed',
            children: [
              {
                title: 'Silicon Growth from a Seed',
                labelKey: 'menu.examples.materialsScience.SiliconGrowthFromSeed',
              },
              { title: 'Copper Growth from a Seed', labelKey: 'menu.examples.materialsScience.CopperGrowthFromSeed' },
              { title: 'Salt Growth from a Seed', labelKey: 'menu.examples.materialsScience.SaltGrowthFromSeed' },
              {
                title: 'Nitrogen Growth from a Seed',
                labelKey: 'menu.examples.materialsScience.NitrogenGrowthFromSeed',
              },
              { title: 'Dry Ice Growth from a Seed', labelKey: 'menu.examples.materialsScience.DryIceGrowthFromSeed' },
            ],
          },
          {
            key: 'evaporation',
            labelKey: 'menu.examples.chemistry.Evaporation',
            children: [
              { title: 'Evaporation of Argon', labelKey: 'menu.examples.chemistry.EvaporationOfArgon' },
              { title: 'Evaporation of Nitrogen', labelKey: 'menu.examples.chemistry.EvaporationOfNitrogen' },
              { title: 'Evaporation of Water', labelKey: 'menu.examples.chemistry.EvaporationOfWater' },
              {
                title: 'Boiling Argon at Constant Pressure',
                labelKey: 'menu.examples.chemistry.BoilingArgonAtConstantPressure',
              },
            ],
          },
          {
            title: 'Annealing of Amorphous Copper',
            labelKey: 'menu.examples.materialsScience.AnnealingOfAmorphousCopper',
          },
        ],
      },
      {
        key: 'hydrocarbon-series',
        labelKey: 'menu.examples.chemistry.HydrocarbonSeries',
        children: [
          { title: 'Alkanes', labelKey: 'menu.examples.chemistry.LinearAlkanes' },
          { title: 'Cycloalkanes', labelKey: 'menu.examples.chemistry.Cycloalkanes' },
          { title: 'Acenes', labelKey: 'menu.examples.chemistry.Acenes' },
        ],
      },
      {
        key: 'organohalogen-compounds',
        labelKey: 'menu.examples.chemistry.OrganohalogenCompounds',
        children: [
          { title: 'Chlorobenzenes', labelKey: 'menu.examples.chemistry.Chlorobenzenes' },
          { title: 'PFAS', labelKey: 'menu.examples.chemistry.ForeverChemicals' },
        ],
      },
      {
        key: 'isomers',
        labelKey: 'menu.examples.chemistry.Isomers',
        children: [
          { title: 'Bu-2-ene Isomers', labelKey: 'menu.examples.chemistry.Bu2EneIsomers' },
          {
            title: 'Enantiomers - Arketamine vs Esketamine',
            labelKey: 'menu.examples.chemistry.ArketamineVsEsketamine',
          },
          { title: 'C₈H₁₈O Isomers', labelKey: 'menu.examples.chemistry.C8H18OIsomers' },
          {
            title: 'Cis–Trans Isomerism of Fatty Acids',
            labelKey: 'menu.examples.chemistry.CisTransIsomerismOfFattyAcids',
          },
        ],
      },
      {
        key: 'chemical-reactions',
        labelKey: 'menu.examples.chemistry.ChemicalReactions',
        children: [
          { title: 'Hydrogen and Oxygen Reaction', labelKey: 'menu.examples.chemistry.HydrogenOxygenReaction' },
          { title: 'Methyl Radicals to Ethanes', labelKey: 'menu.examples.chemistry.MethylRadicalsToEthanes' },
          {
            title: 'Hydrogen Dissociation Equilibrium',
            labelKey: 'menu.examples.chemistry.HydrogenDissociationEquilibrium',
          },
        ],
      },
      { title: 'Polymers', labelKey: 'menu.examples.chemistry.Polymers' },
      { title: 'Copolymers', labelKey: 'menu.examples.chemistry.Copolymers' },
      { title: 'Designing a Latex Paint Binder', labelKey: 'menu.examples.chemistry.DesigningALatexPaintBinder' },
      { title: 'Calorimetry Argon Gas', labelKey: 'menu.examples.chemistry.CalorimetryArgonGas' },
    ],
  },
  {
    key: 'biology',
    labelKey: 'menu.examples.biologySubMenu',
    children: [
      { title: 'Alpha Helix', labelKey: 'menu.examples.biology.ProteinAlphaHelix' },
      { title: 'DNA Double Helix', labelKey: 'menu.examples.biology.DNADoubleHelix' },
      { title: 'Levels of Protein Structure', labelKey: 'menu.examples.biology.LevelsOfProteinStructure' },
      { title: 'Lysozyme', labelKey: 'menu.examples.biology.Lysozyme' },
      { title: 'Green Fluorescent Protein', labelKey: 'menu.examples.biology.GreenFluorescentProtein' },
      { title: 'Flexibility of HIV-1 Protease', labelKey: 'menu.examples.biology.FlexibilityOfHIV1Protease' },
      { title: 'Myoglobin and Oxygen', labelKey: 'menu.examples.biology.MyoglobinAndOxygen' },
      { title: 'Insulin', labelKey: 'menu.examples.biology.Insulin' },
      { title: 'Sickle-Cell Hemoglobin', labelKey: 'menu.examples.biology.SickleCellHemoglobin' },
      { title: 'Prion Protein (Predicted)', labelKey: 'menu.examples.biology.PrionProteinPredicted' },
      { title: 'Trypsin and Its Specificity Pocket', labelKey: 'menu.examples.biology.TrypsinSpecificityPocket' },
    ],
  },
  {
    key: 'materials-science',
    labelKey: 'menu.examples.materialsScienceSubMenu',
    children: [
      {
        key: 'ionic-crystals',
        labelKey: 'menu.examples.materialsScience.IonicCrystals',
        children: [
          { title: 'Rocksalt Oxides', labelKey: 'menu.examples.materialsScience.RocksaltOxides' },
          { title: 'Wurtzite', labelKey: 'menu.examples.materialsScience.Wurtzite' },
          { title: 'Rutiles', labelKey: 'menu.examples.materialsScience.Rutiles' },
          { title: 'Fluorites', labelKey: 'menu.examples.materialsScience.Fluorites' },
          { title: 'Alkali Halides', labelKey: 'menu.examples.materialsScience.AlkaliHalides' },
        ],
      },
      {
        key: 'metallic-crystals',
        labelKey: 'menu.examples.materialsScience.MetallicCrystals',
        children: [
          { title: 'FCC Metals', labelKey: 'menu.examples.materialsScience.FCCMetals' },
          { title: 'BCC Metals', labelKey: 'menu.examples.materialsScience.BCCMetals' },
          { title: 'HCP Metals', labelKey: 'menu.examples.materialsScience.HCPMetals' },
          { title: 'Intermetallics', labelKey: 'menu.examples.materialsScience.Intermetalics' },
        ],
      },
      {
        key: 'covalent-crystals',
        labelKey: 'menu.examples.materialsScience.CovalentCrystals',
        children: [
          { title: 'Pure Covalent Crystals', labelKey: 'menu.examples.materialsScience.PureCovalentCrystals' },
          { title: 'Compound Covalent Crystals', labelKey: 'menu.examples.materialsScience.CompoundCovalentCrystals' },
          { title: 'Molecular Sieves', labelKey: 'menu.examples.materialsScience.MolecularSieves' },
          {
            title: 'Designing a Carbon Dioxide Sieve',
            labelKey: 'menu.examples.materialsScience.DesigningACarbonDioxideSieve',
          },
        ],
      },
      {
        key: 'molecular-crystals',
        labelKey: 'menu.examples.materialsScience.MolecularCrystals',
        children: [
          { title: 'Ice Structure', labelKey: 'menu.examples.materialsScience.IceStructure' },
          { title: 'Simple Molecular Crystals', labelKey: 'menu.examples.materialsScience.SimpleMolecularCrystals' },
        ],
      },
      {
        key: 'semiconductors',
        labelKey: 'menu.examples.materialsScience.Semiconductors',
        children: [
          {
            title: 'Designing a Red-Laser Alloy',
            labelKey: 'menu.examples.materialsScience.DesigningARedLaserAlloy',
          },
          {
            title: 'Gallium Arsenide on Silicon',
            labelKey: 'menu.examples.materialsScience.GalliumArsenideOnSilicon',
          },
        ],
      },
      {
        key: 'heat-conduction',
        labelKey: 'menu.examples.materialsScience.HeatConduction',
        children: [
          { title: 'Heat Conduction in Salt', labelKey: 'menu.examples.materialsScience.HeatConductionInSalt' },
          {
            title: 'Heat Conduction in Silicon',
            labelKey: 'menu.examples.materialsScience.HeatConductionInSilicon',
          },
        ],
      },
      {
        key: 'layered-materials',
        labelKey: 'menu.examples.materialsScience.LayeredMaterials',
        children: [
          { title: 'Graphite Layers', labelKey: 'menu.examples.materialsScience.Graphite' },
          { title: 'Graphene', labelKey: 'menu.examples.materialsScience.Graphenes' },
        ],
      },
      {
        key: 'soft-matter',
        labelKey: 'menu.examples.materialsScience.SoftMatter',
        children: [
          {
            title: 'Modeling Liquid Crystals with Gay-Berne Particles',
            labelKey: 'menu.examples.materialsScience.LiquidCrystalsGayBerne',
          },
          {
            title: 'Mixing Long and Short Rods in a Liquid Crystal',
            labelKey: 'menu.examples.materialsScience.LiquidCrystalMixture',
          },
          { title: 'A Guest-Host Liquid Crystal Display', labelKey: 'menu.examples.materialsScience.GuestHostDisplay' },
          { title: 'Smectic Layers in a Liquid Crystal', labelKey: 'menu.examples.materialsScience.SmecticLayers' },
          {
            title: 'A Polymer Melt Near Its Glass Transition',
            labelKey: 'menu.examples.materialsScience.PolymerMeltBeads',
          },
          {
            title: 'A Sequence That Folds and One That Does Not',
            labelKey: 'menu.examples.materialsScience.HpFoldingBeads',
          },
          {
            title: 'Micelle Formation',
            labelKey: 'menu.examples.materialsScience.MicelleFormationBeads',
          },
          {
            title: 'A Protein That Folds and Holds',
            labelKey: 'menu.examples.materialsScience.GoFoldingBeads',
          },
        ],
      },
    ],
  },
  {
    key: 'nanotechnology',
    labelKey: 'menu.examples.nanotechnologySubMenu',
    children: [
      {
        key: 'carbon-nanotubes',
        labelKey: 'menu.examples.nanotechnology.CarbonNanotubes',
        children: [
          {
            title: 'Water Molecules in a Carbon Nanotube',
            labelKey: 'menu.examples.nanotechnology.CarbonNanotube',
          },
          {
            title: 'Carbon Nanotubes: Metallic or Semiconducting?',
            labelKey: 'menu.examples.nanotechnology.NanotubesMetalOrSemiconductor',
          },
          { title: 'Multiwall Carbon Nanotubes', labelKey: 'menu.examples.nanotechnology.MultiwallCarbonNanotubes' },
          { title: 'Stretching a Carbon Nanotube', labelKey: 'menu.examples.nanotechnology.StretchingACarbonNanotube' },
          {
            title: 'Designing a Nanotube Strain Sensor',
            labelKey: 'menu.examples.nanotechnology.DesigningANanotubeStrainSensor',
          },
          { title: 'Carbon Nanobuds', labelKey: 'menu.examples.nanotechnology.CarbonNanobuds' },
          { title: 'A Nanotube Y-Junction', labelKey: 'menu.examples.nanotechnology.NanotubeYJunction' },
        ],
      },
      {
        key: 'fullerenes',
        labelKey: 'menu.examples.nanotechnology.Fullerenes',
        children: [
          { title: 'Buckyballs', labelKey: 'menu.examples.nanotechnology.Buckyballs' },
          { title: 'The Fullerene Family', labelKey: 'menu.examples.nanotechnology.FullereneFamily' },
        ],
      },
      {
        key: 'graphene-nanostructures',
        labelKey: 'menu.examples.nanotechnology.GrapheneNanostructures',
        children: [
          { title: 'A Graphene Nanopore Membrane', labelKey: 'menu.examples.nanotechnology.GrapheneNanoporeMembrane' },
          {
            title: 'Graphene Nanoribbon Families',
            labelKey: 'menu.examples.nanotechnology.GrapheneNanoribbonFamilies',
          },
        ],
      },
      {
        key: 'quantum-confinement',
        labelKey: 'menu.examples.nanotechnology.QuantumConfinement',
        children: [
          { title: 'Why Silicon Glows When It Is Small', labelKey: 'menu.examples.nanotechnology.WhySiliconGlows' },
          {
            title: 'Quantum Dots Versus Nanowires',
            labelKey: 'menu.examples.nanotechnology.QuantumDotsVersusNanowires',
          },
          {
            title: 'Choosing a Cadmium-Free Quantum Dot',
            labelKey: 'menu.examples.nanotechnology.ChoosingACadmiumFreeQuantumDot',
          },
        ],
      },
      { title: 'Melting of Gold Nanoparticles', labelKey: 'menu.examples.nanotechnology.MeltingOfGoldNanoparticles' },
      { title: 'A Nanocar on Gold', labelKey: 'menu.examples.nanotechnology.NanocarOnGold' },
    ],
  },
  {
    key: 'biotechnology',
    labelKey: 'menu.examples.biotechnologySubMenu',
    children: [{ title: 'HIV-1 Protease Inhibitor', labelKey: 'menu.examples.biotechnology.HIV1ProteaseInhibitor' }],
  },
];

export interface FlatExample {
  title: string;
  /** Top-level group, humanized (e.g. "Materials Science"). */
  category: string;
  /** Nested group when present, humanized (e.g. "Ionic Crystals"); omitted otherwise. */
  subcategory?: string;
}

// Turn a group key like 'materials-science' / 'ionic-crystals' into a
// human-readable label without dragging i18n into the agent layer.
const humanizeKey = (key: string): string =>
  key
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

/**
 * Flatten the example tree into a list the agent can scan/recommend from.
 * Each entry carries the project title plus its (humanized) category path.
 */
export function flattenExamples(): FlatExample[] {
  const out: FlatExample[] = [];
  const walk = (nodes: Array<ExampleLeaf | ExampleGroup>, category: string, subcategory?: string) => {
    for (const node of nodes) {
      if (isExampleGroup(node)) {
        // A nested group becomes the subcategory; deeper nesting isn't used,
        // but recurse safely just in case.
        walk(node.children, category, subcategory ?? humanizeKey(node.key));
      } else {
        out.push(subcategory ? { title: node.title, category, subcategory } : { title: node.title, category });
      }
    }
  };
  for (const g of EXAMPLE_GROUPS) walk(g.children, humanizeKey(g.key));
  return out;
}
